import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, FileText, Download, Clock } from 'lucide-react';
import { useTheme, money } from '../lib/theme';
import { getDashboard, getScore, getBookkeeperProgress } from '../lib/api';
import { ConfidencePill } from '../components/ConfidencePill';
import ScoreCard from '../components/ScoreCard';

function greet() {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
}

function longDate() {
  return new Date().toLocaleDateString(undefined, {
    weekday: 'long', month: 'long', day: 'numeric',
  });
}

export default function Dashboard() {
  const { colors, effective } = useTheme();
  const [data, setData] = useState(null);
  const [score, setScore] = useState(null);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [r, s, p] = await Promise.all([
        getDashboard(),
        getScore().catch(() => null),
        getBookkeeperProgress().catch(() => null),
      ]);
      setData(r);
      setScore(s);
      setProgress(p);
    } catch (err) {
      // Dashboard is read-only surface — surface error via UI fallback but log
      console.error('Failed to load dashboard:', err);
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="dashboard-page">
      <div className="mb-8">
        <h1
          className="text-3xl font-bold tracking-tight"
          style={{ color: colors.text }}
          data-testid="dashboard-greeting"
        >
          {greet()}
        </h1>
        <p className="text-sm mt-1" style={{ color: colors.textMuted }}>{longDate()}</p>
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <div
            className="w-8 h-8 border-2 rounded-full animate-spin"
            style={{ borderColor: colors.border, borderTopColor: colors.accent }}
          />
        </div>
      ) : data ? (
        <>
          {/* Clasifi Score card */}
          {score ? (
            <div className="mb-6">
              <ScoreCard
                score={score}
                colors={colors}
                dark={effective === 'dark'}
                progress={progress}
              />
            </div>
          ) : null}

          {/* Stat cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="stat-grid">
            <Link
              to="/app/receipts?status=pending"
              data-testid="card-pending"
              className="rounded-xl border p-5 transition-all hover:shadow-md focus-visible:outline-none focus-visible:ring-2"
              style={{ backgroundColor: colors.surface, borderColor: colors.border }}
            >
              <p className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: colors.textMuted }}>
                Pending review
              </p>
              <p className="text-3xl font-bold mt-2 tabular-nums" style={{ color: colors.text }}>
                {data.pending_count}
              </p>
              <p className="text-xs mt-1" style={{ color: colors.textMuted }}>Tap to review</p>
            </Link>

            <Link
              to="/app/receipts"
              data-testid="card-month"
              className="rounded-xl border p-5 transition-all hover:shadow-md focus-visible:outline-none focus-visible:ring-2"
              style={{ backgroundColor: colors.surface, borderColor: colors.border }}
            >
              <p className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: colors.textMuted }}>
                This month
              </p>
              <p className="text-3xl font-bold mt-2 tabular-nums" style={{ color: colors.text }}>
                {data.this_month.count}
              </p>
              <p className="text-xs mt-1 tabular-nums" style={{ color: colors.textMuted }}>
                {money(data.this_month.total)} spent
              </p>
            </Link>

            <Link
              to="/app/export"
              data-testid="card-tax"
              className="rounded-xl border p-5 transition-all hover:shadow-md focus-visible:outline-none focus-visible:ring-2"
              style={{ backgroundColor: colors.surface, borderColor: colors.border }}
            >
              <p className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: colors.textMuted }}>
                Taxes this year
              </p>
              <p className="text-3xl font-bold mt-2 tabular-nums truncate" style={{ color: colors.text }}>
                {money(data.this_year.gst + data.this_year.qst)}
              </p>
              <p className="text-xs mt-1 tabular-nums truncate" style={{ color: colors.textMuted }}>
                GST {money(data.this_year.gst)} · QST {money(data.this_year.qst)}
              </p>
            </Link>

            <Link
              to="/app/receipts?status=approved"
              data-testid="card-approved"
              className="rounded-xl border p-5 transition-all hover:shadow-md focus-visible:outline-none focus-visible:ring-2"
              style={{ backgroundColor: colors.surface, borderColor: colors.border }}
            >
              <p className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: colors.textMuted }}>
                Approved this year
              </p>
              <p className="text-3xl font-bold mt-2 tabular-nums" style={{ color: colors.text }}>
                {data.approved_this_year}
              </p>
              <p className="text-xs mt-1 tabular-nums" style={{ color: colors.textMuted }}>
                {money(data.this_year.total)} total
              </p>
            </Link>
          </div>

          {/* Recent activity */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <h2
                className="text-[11px] font-semibold uppercase tracking-wider"
                style={{ color: colors.textMuted }}
              >
                Recent activity
              </h2>
              <Link
                to="/app/receipts"
                className="text-sm font-medium flex items-center gap-1 transition-opacity hover:opacity-70"
                style={{ color: colors.accent }}
                data-testid="view-all-link"
              >
                View all <ArrowRight size={14} />
              </Link>
            </div>

            {data.recent.length === 0 ? (
              <div
                className="flex flex-col items-center justify-center py-12 rounded-xl border border-dashed"
                style={{ backgroundColor: colors.surface, borderColor: colors.border }}
                data-testid="empty-recent"
              >
                <FileText size={32} style={{ color: colors.textMuted }} />
                <p className="text-sm mt-3" style={{ color: colors.textMuted }}>
                  Your recent receipts will appear here
                </p>
              </div>
            ) : (
              <div
                className="rounded-xl border overflow-hidden"
                style={{ backgroundColor: colors.surface, borderColor: colors.border }}
                data-testid="recent-list"
              >
                {data.recent.map((r, i) => (
                  <Link
                    key={r.id}
                    to={`/receipts/${r.id}`}
                    data-testid={`recent-${r.id}`}
                    className="flex items-center gap-4 px-5 py-3.5 transition-colors hover:opacity-80"
                    style={{
                      borderBottom: i < data.recent.length - 1 ? `1px solid ${colors.border}` : 'none',
                    }}
                  >
                    <div
                      className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                      style={{ backgroundColor: colors.accentSoft }}
                    >
                      <Receipt size={16} style={{ color: colors.accent }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold truncate" style={{ color: colors.text }}>
                        {r.vendor ?? 'Unknown vendor'}
                      </p>
                      <p className="text-xs truncate" style={{ color: colors.textMuted }}>
                        {r.date ?? '—'}{r.category ? ` · ${r.category}` : ''}
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-sm font-semibold tabular-nums" style={{ color: colors.text }}>
                        {money(r.total)}
                      </p>
                      {r.status !== 'pending' && (
                        <span
                          className="text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full"
                          style={{
                            backgroundColor: r.status === 'approved' ? colors.successSoft : colors.errorSoft,
                            color: r.status === 'approved' ? colors.success : colors.error,
                          }}
                        >
                          {r.status}
                        </span>
                      )}
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Quick export link */}
          <div className="flex justify-center">
            <Link
              to="/app/export"
              data-testid="dashboard-export-link"
              className="text-sm font-semibold flex items-center gap-1.5 transition-opacity hover:opacity-70"
              style={{ color: colors.accent }}
            >
              <Download size={14} />
              Export CSV
            </Link>
          </div>
        </>
      ) : (
        <p className="text-center py-20" style={{ color: colors.textMuted }}>
          Unable to load dashboard. Please try refreshing.
        </p>
      )}
    </div>
  );
}

// tiny helper used in recent list
function Receipt({ size, style }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1Z"/>
      <path d="M16 8h-6a2 2 0 1 0 0 4h4a2 2 0 1 1 0 4H8"/>
      <path d="M12 17.5v.5m0-12v.5"/>
    </svg>
  );
}
